import { addDays, type Locale } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { fr, enUS, es, it } from "date-fns/locale";

const LOCALE_MAP: Record<string, Locale> = {
  fr,
  en: enUS,
  es,
  it,
};

/**
 * Get date-fns locale from i18n language code.
 * Falls back to French (primary app language).
 */
export function getDateLocale(lang?: string): Locale {
  if (!lang) return fr;
  return LOCALE_MAP[lang] || LOCALE_MAP[lang.split("-")[0]] || fr;
}

/**
 * Whether the given i18n language conventionally uses a 12-hour clock.
 * English (en, en-US, …) → 12h with AM/PM; French/Spanish/Italian (and the
 * French fallback) → 24h.
 */
export function uses12HourClock(lang?: string): boolean {
  return lang?.split("-")[0] === "en";
}

/**
 * date-fns time-of-day pattern for the given i18n language: `h:mm a`
 * (e.g. "2:30 PM") for 12-hour locales, `HH:mm` (e.g. "14:30") otherwise.
 * Use this everywhere a time is DISPLAYED; canonical values exchanged with
 * the API or `<input>` state stay 24-hour `HH:mm`.
 */
export function timePattern(lang?: string): string {
  return uses12HourClock(lang) ? "h:mm a" : "HH:mm";
}

/**
 * Format a real UTC instant as a precise relative date/time string in the
 * company timezone. Day buckets (today / yesterday / this year) are decided
 * in the company tz, so a 23:00 UTC message is "yesterday" for an LA tenant
 * browsed from Paris (and vice versa).
 *
 * Examples: "14:30", "Hier 09:15", "25 nov., 14:30", "25 nov. 2024, 14:30"
 * (12-hour in English: "2:30 PM", "Yesterday 9:15 AM", …)
 */
export function formatPreciseRelativeDate(
  date: Date,
  lang: string | undefined,
  tz: string,
): string {
  if (isNaN(date.getTime())) return "";

  const locale = getDateLocale(lang);
  const now = new Date();

  const dateYmd = formatInTimeZone(date, tz, "yyyy-MM-dd");
  const todayYmd = formatInTimeZone(now, tz, "yyyy-MM-dd");
  // Yesterday's calendar day in the company tz: anchor today's y/m/d at UTC
  // midnight, step back one day, read it back in UTC. Pure calendar math —
  // no browser-local tz leaks in (date-fns `format` would render in browser tz).
  const [ty, tm, td] = todayYmd.split("-").map(Number);
  const yesterdayYmd = formatInTimeZone(
    addDays(Date.UTC(ty, tm - 1, td), -1),
    "UTC",
    "yyyy-MM-dd",
  );

  const time = timePattern(lang);
  if (dateYmd === todayYmd) {
    return formatInTimeZone(date, tz, time, { locale });
  }
  if (dateYmd === yesterdayYmd) {
    // Normalize region subtags (e.g. "fr-FR", "es-MX") to the base language so
    // the label isn't silently rendered in English for regional locales.
    const baseLang = lang?.split("-")[0];
    const yesterdayLabel =
      baseLang === "fr"
        ? "Hier"
        : baseLang === "es"
          ? "Ayer"
          : baseLang === "it"
            ? "Ieri"
            : "Yesterday";
    return `${yesterdayLabel} ${formatInTimeZone(date, tz, time, { locale })}`;
  }
  if (todayYmd.slice(0, 4) === dateYmd.slice(0, 4)) {
    return formatInTimeZone(date, tz, `d MMM, ${time}`, { locale });
  }
  return formatInTimeZone(date, tz, `d MMM yyyy, ${time}`, { locale });
}

/**
 * String overload of `formatPreciseRelativeDate`. Treats a naive backend
 * timestamp as UTC by appending "Z" when no offset marker is present.
 *
 * @param dateString - ISO 8601 date string from backend (UTC, may omit "Z")
 * @param lang - i18n language code (e.g. "fr", "en", "es", "it")
 * @param tz - IANA company timezone (from `useCompanyTimezone()`)
 */
export function formatPreciseRelativeTime(
  dateString: string | null | undefined,
  lang: string | undefined,
  tz: string,
): string {
  if (!dateString) return "";

  try {
    let utcDateString = dateString;
    if (!dateString.endsWith("Z") && !dateString.includes("+")) {
      utcDateString = dateString + "Z";
    }
    const date = new Date(utcDateString);
    return formatPreciseRelativeDate(date, lang, tz);
  } catch {
    return "";
  }
}
